import { Alert, Box, Stack, TextField, Typography } from "@mui/material"
import { t } from "i18next"
import React, { FC } from "react"
import { Locker } from "shared/locker"
import { useUpdateThingsMutation } from "../features/things/thingsApi"
import { isUsableModuleMapping } from "./lockerModuleOrder"
import { Sl2Btn } from "./Sl2Btn"

const width = 150

// One row of the editor: a module the admin can position. The editor is
// version-agnostic by construction (LOCKER-390) — a V1 caller derives these
// from `sstLocker.locker_grid`, a V2 caller from `sst2Locker.templates_association`
// in `visual_order` — so all it needs is a display name per module.
export type MappableModule = { name?: string | null }

const LockerModuleOrderMapping: FC<{
  moduleMapping?: { sstIdx: number; sl2Idx: number }[]
  modules?: MappableModule[]
  locker: Partial<Locker>
}> = ({ moduleMapping = [], modules = [], locker }) => {
  const identity = modules.map((_, index: number) => ({
    sstIdx: index,
    sl2Idx: index,
  }))
  // A mapping that is not a total permutation of the CURRENT module list —
  // saved when the locker had a different module count, or with a duplicate /
  // out-of-range index — would render the editor against a shape it cannot
  // save back. Fall back to identity, matching what the dashboards render for
  // the same input (LOCKER-390 N3: no migration, no repair).
  const mapping = isUsableModuleMapping(moduleMapping, modules.length)
    ? moduleMapping
    : identity

  const [localMapping, setLocalMapping] = React.useState(mapping)
  const [error, setError] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)

  const [updateThings] = useUpdateThingsMutation()

  const handleMappingChange = (
    index: number,
    field: "sstIdx" | "sl2Idx",
    value: number,
  ) => {
    const maxIndex = modules.length - 1
    if (value < 0 || value > maxIndex) {
      return // Prevent invalid values
    }
    setLocalMapping((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    )
  }

  const handleSave = async () => {
    const sstIndexes = localMapping.map((m) => m.sstIdx)
    const uniqueSstIndexes = new Set(sstIndexes)
    if (uniqueSstIndexes.size !== sstIndexes.length) {
      setError("SST Indexes must be unique.")
      return
    }
    const sortedSstIndexes = [...uniqueSstIndexes].sort((a, b) => a - b)
    if (JSON.stringify(sstIndexes) !== JSON.stringify(sortedSstIndexes)) {
      setError("SST Indexes must be ordered from 0 to n.")
      return
    }

    const sl2Indexes = localMapping.map((m) => m.sl2Idx)
    const uniqueSl2Indexes = new Set(sl2Indexes)
    if (uniqueSl2Indexes.size !== sl2Indexes.length) {
      setError("SL2 Indexes must be unique.")
      return
    }
    const sortedSl2Indexes = [...uniqueSl2Indexes].sort((a, b) => a - b)
    const expectedSl2Indexes = Array.from(
      { length: modules.length },
      (_, i) => i,
    )
    if (
      JSON.stringify(sortedSl2Indexes) !== JSON.stringify(expectedSl2Indexes)
    ) {
      setError("SL2 Indexes must cover all indexes from 0 to n.")
      return
    }

    setError(null)
    setIsLoading(true)
    const patch = {
      id: locker.id,
      thingTypeMrn: locker.thingTypeMrn,
      tenantId: locker.tenantId,
      moduleMapping: localMapping,
    }
    try {
      await updateThings({
        body: { things: [patch] },
        params: { tenantId: locker.tenantId },
      }).unwrap()
      setIsLoading(false)
    } catch (err) {
      setError("Failed to update locker. Please try again.")
      setIsLoading(false)
    }
  }

  return (
    <Box>
      {localMapping.length === 1 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("lockerModuleOrderMapping.warningSingleItem")}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack direction="column" spacing={1}>
        {modules.map((module, index) => (
          <Stack
            key={index}
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography>
              {module.name ||
                t("lockerModuleOrderMapping.module", { index: index + 1 })}
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                label={t("lockerModuleOrderMapping.sstIndex")}
                type="number"
                size="small"
                sx={{
                  width: `${width}px`,
                }}
                value={localMapping[index]?.sstIdx ?? 0}
                onChange={(e) =>
                  handleMappingChange(index, "sstIdx", Number(e.target.value))
                }
                disabled={true}
              />
              <TextField
                label={t("lockerModuleOrderMapping.sl2Index")}
                type="number"
                size="small"
                sx={{
                  width: `${width}px`,
                }}
                value={localMapping[index]?.sl2Idx ?? 0}
                onChange={(e) =>
                  handleMappingChange(index, "sl2Idx", Number(e.target.value))
                }
                disabled={localMapping.length === 1}
                inputProps={{ min: 0, max: modules.length - 1 }}
              />
            </Stack>
          </Stack>
        ))}
      </Stack>
      {localMapping.length > 1 && (
        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
          <Sl2Btn
            variant="contained"
            onClick={handleSave}
            size="small"
            disabled={isLoading}
          >
            {isLoading
              ? t("lockerModuleOrderMapping.saving")
              : t("lockerModuleOrderMapping.saveMapping")}
          </Sl2Btn>
        </Stack>
      )}
    </Box>
  )
}

export default LockerModuleOrderMapping
